import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthHeader, serverUrl } from ".";
import { PaginationType } from "../types/filter";

export const useQnaQuestions = ({
  filters,
  pagination,
}: {
  filters: Record<string, string>;
  pagination: PaginationType;
}) => {
  return useQuery({
    queryKey: ["qna", filters, pagination],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/qna?${new URLSearchParams({
          filters: JSON.stringify(filters),
          start: (pagination.page * pagination.size).toString(),
          limit: pagination.size.toString(),
        }).toString()}`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useCreateQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key === "files" && value) {
          Array.from(value as File[]).forEach((file) => {
            formData.append("files", file);
          });
        } else if (typeof value === "string") {
          formData.append(key, value);
        } else {
          formData.append(key, JSON.stringify(value));
        }
      });

      const response = await axios.post(
        `${serverUrl}/qna/`,
        formData,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qna"] });
    },
  });
};

export const useQnaDetail = ({ id }: { id: string }) => {
  return useQuery({
    queryKey: ["qna", id],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/qna/question/${id}`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useCreateAnswer = ({ id }: { id: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(
        `${serverUrl}/qna/question/${id}`,
        data,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qna"] });
    },
  });
};

export const useCancelAnswer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      await axios.post(
        `${serverUrl}/qna/question/cancel/${id}`,
        undefined,
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qna"] });
    },
  });
};
